import { Link } from 'react-router-dom';
import { useState } from 'react';
import { motion } from 'framer-motion';
import {
  LinkIcon,
  ChartBarIcon,
  BellAlertIcon,
  CurrencyDollarIcon,
  RocketLaunchIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';
import { FaYoutube, FaTiktok, FaInstagram, FaXTwitter } from 'react-icons/fa6';
import BrandLogo from '../components/shared/BrandLogo';
import { BRAND_HERO_GRADIENT } from '../components/shared/SectionTitle';

const platformGuides = [
  {
    id: 'YOUTUBE',
    name: 'YouTube',
    icon: FaYoutube,
    color: 'text-red-600',
    bgColor: 'bg-red-50 dark:bg-red-900/20',
    borderColor: 'border-red-200 dark:border-red-800',
    note: 'All you need is a Google account that owns a YouTube channel. OmniConnX only requests read-only access to your channel and analytics.',
    steps: [
      { label: 'Click Connect on YouTube', detail: 'In Settings → Integrations (or the Analytics Platforms card), click Connect next to YouTube.' },
      { label: 'Sign in with Google', detail: 'Choose the Google account that owns the YouTube channel you want to track.' },
      { label: 'Approve access', detail: 'On Google’s secure consent screen, allow OmniConnX to view your channel and analytics. You can revoke access anytime from your Google Account.' },
      { label: 'You’re connected', detail: 'You’re returned to OmniConnX and your channel appears right away. Views, subscribers and engagement start syncing within a few minutes.' },
    ],
  },
  {
    id: 'TIKTOK',
    name: 'TikTok',
    icon: FaTiktok,
    color: 'text-gray-900 dark:text-white',
    bgColor: 'bg-gray-50 dark:bg-gray-800',
    borderColor: 'border-gray-200 dark:border-gray-700',
    note: 'Use the TikTok account you post your videos from.',
    steps: [
      { label: 'Click Connect on TikTok', detail: 'In Settings → Integrations (or the Analytics Platforms card), click Connect next to TikTok.' },
      { label: 'Log in to TikTok', detail: 'Sign in with the TikTok account you create content on.' },
      { label: 'Authorize OmniConnX', detail: 'TikTok shows exactly what you’re sharing — your profile, video list and stats. Tap Authorize to continue.' },
      { label: 'You’re connected', detail: 'You land back in OmniConnX with TikTok linked, and your videos and their stats sync shortly after.' },
    ],
  },
  {
    id: 'INSTAGRAM',
    name: 'Instagram',
    icon: FaInstagram,
    color: 'text-pink-600',
    bgColor: 'bg-pink-50 dark:bg-pink-900/20',
    borderColor: 'border-pink-200 dark:border-pink-800',
    note: 'Instagram connects through Facebook, so your Instagram needs to be a Business or Creator (professional) account linked to a Facebook Page.',
    steps: [
      { label: 'Click Connect on Instagram', detail: 'In Settings → Integrations (or the Analytics Platforms card), click Connect next to Instagram.' },
      { label: 'Continue with Facebook', detail: 'Sign in with the Facebook account that manages your Instagram professional account.' },
      { label: 'Choose your account & Page', detail: 'Select the Instagram professional account and its linked Facebook Page, then approve the requested permissions.' },
      { label: 'You’re connected', detail: 'You’re returned to OmniConnX and your Instagram insights begin syncing automatically.' },
    ],
  },
  {
    id: 'TWITTER',
    name: 'Twitter / X',
    icon: FaXTwitter,
    color: 'text-gray-900 dark:text-white',
    bgColor: 'bg-gray-50 dark:bg-gray-800',
    borderColor: 'border-gray-200 dark:border-gray-700',
    note: 'Use the X account you want to track. OmniConnX requests read access to your profile and posts.',
    steps: [
      { label: 'Click Connect on X', detail: 'In Settings → Integrations (or the Analytics Platforms card), click Connect next to X.' },
      { label: 'Log in to X', detail: 'Sign in with the X (Twitter) account you want to connect.' },
      { label: 'Authorize the app', detail: 'On X’s authorization screen, approve read access to your profile and posts.' },
      { label: 'You’re connected', detail: 'You’re returned to OmniConnX and your X metrics start syncing.' },
    ],
  },
];

const PlatformGuideAccordion = () => {
  const [openId, setOpenId] = useState('YOUTUBE');

  return (
    <div className="space-y-3">
      {platformGuides.map((platform) => {
        const PlatformIcon = platform.icon;
        const isOpen = openId === platform.id;
        return (
          <div
            key={platform.id}
            className={`border rounded-xl overflow-hidden transition-colors ${platform.borderColor}`}
          >
            <button
              onClick={() => setOpenId(isOpen ? null : platform.id)}
              className={`w-full flex items-center justify-between p-4 ${platform.bgColor} transition-colors`}
            >
              <div className="flex items-center gap-3">
                <PlatformIcon className={`w-6 h-6 ${platform.color}`} />
                <span className="font-semibold text-gray-900 dark:text-white">{platform.name}</span>
              </div>
              <ChevronDownIcon
                className={`w-5 h-5 text-gray-500 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
              />
            </button>

            {isOpen && (
              <div className="p-5 bg-white dark:bg-dark-900 space-y-4">
                <ol className="space-y-4">
                  {platform.steps.map((step, i) => (
                    <li key={i} className="flex gap-4">
                      <span className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold text-white ${platform.id === 'YOUTUBE' ? 'bg-red-500' : platform.id === 'INSTAGRAM' ? 'bg-pink-500' : 'bg-gray-700'}`}>
                        {i + 1}
                      </span>
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white text-sm">{step.label}</p>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">{step.detail}</p>
                      </div>
                    </li>
                  ))}
                </ol>
                {platform.note && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-dark-800 border border-gray-200 dark:border-dark-700 rounded-lg p-3">
                    <span className="font-semibold text-gray-700 dark:text-gray-300">Good to know:</span> {platform.note}
                  </p>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

const HowItWorksPage = () => {
  const steps = [
    {
      number: '01',
      icon: LinkIcon,
      title: 'Connect Your Platforms',
      description: 'Link your YouTube, TikTok, Instagram, and other creator accounts in minutes. Secure OAuth integration ensures your data stays safe.',
      image: '🔗',
    },
    {
      number: '02',
      icon: ChartBarIcon,
      title: 'Track Your Analytics',
      description: 'Get a unified dashboard showing all your metrics in one place. Views, engagement, growth trends, and more across all platforms.',
      image: '📊',
    },
    {
      number: '03',
      icon: BellAlertIcon,
      title: 'Monitor Your Health',
      description: 'AI-powered anomaly detection alerts you to unusual patterns. Stay ahead of drops in engagement or reach before they become problems.',
      image: '🏥',
    },
    {
      number: '04',
      icon: CurrencyDollarIcon,
      title: 'Manage Revenue',
      description: 'Track earnings from all sources, reconcile with payment platforms, and get insights into your revenue streams.',
      image: '💰',
    },
    {
      number: '05',
      icon: RocketLaunchIcon,
      title: 'Grow Your Business',
      description: 'Use data-driven insights to optimize content, negotiate better deals, and scale your creator business efficiently.',
      image: '🚀',
    },
  ];

  const features = [
    {
      title: 'Multi-Platform Dashboard',
      description: 'See all your creator metrics in one unified dashboard. No more switching between apps.',
      icon: '📱',
    },
    {
      title: 'Real-Time Updates',
      description: 'Get instant notifications when important metrics change. Stay on top of your performance.',
      icon: '⚡',
    },
    {
      title: 'AI-Powered Insights',
      description: 'Machine learning algorithms detect anomalies and provide actionable recommendations.',
      icon: '🤖',
    },
    {
      title: 'Revenue Reconciliation',
      description: 'Automatically match earnings from platforms with payment records. Never miss a payment.',
      icon: '💳',
    },
    {
      title: 'Brand Deal Management',
      description: 'Track negotiations, deliverables, and payments for all your sponsorships in one place.',
      icon: '🤝',
    },
    {
      title: 'Export & Reports',
      description: 'Generate professional reports in PDF or CSV format for stakeholders and tax purposes.',
      icon: '📄',
    },
  ];

  const useCases = [
    {
      role: 'Solo Creators',
      description: 'Manage your entire creator business from one dashboard. Save hours every week on admin tasks.',
      benefits: ['Time savings', 'Better insights', 'Revenue tracking'],
    },
    {
      role: 'Creator Teams',
      description: 'Collaborate with editors, managers, and assistants. Everyone stays on the same page.',
      benefits: ['Team collaboration', 'Role-based access', 'Shared analytics'],
    },
    {
      role: 'Agencies',
      description: 'Manage multiple creator accounts from a single platform. White-label options available.',
      benefits: ['Multi-account management', 'Client reports', 'Custom branding'],
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-dark-950">
      {/* Header */}
      <nav className="bg-white dark:bg-dark-900 border-b border-gray-200 dark:border-dark-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center">
              <BrandLogo size="md" />
            </Link>

            <div className="flex items-center space-x-4">
              <Link to="/" className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white">
                Home
              </Link>
              <Link to="/login" className="btn btn-secondary">
                Login
              </Link>
              <Link to="/register" className="btn bg-gradient-to-r from-pink-500 to-violet-600 hover:from-pink-600 hover:to-violet-700 text-white shadow-md">
                Sign Up
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative overflow-hidden py-20">
        {/* Brand gradient — matches the OmniConnX logo (orange → pink → violet → blue) */}
        <div className="absolute inset-0" style={{ background: BRAND_HERO_GRADIENT }} />
        <div
          className="absolute inset-0 opacity-10"
          style={{ backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)', backgroundSize: '20px 20px' }}
        />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl md:text-5xl font-bold text-white mb-6"
          >
            How OmniConnX Works
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-xl text-white/80 max-w-3xl mx-auto"
          >
            Manage your entire creator business from one powerful platform. Here's how it works.
          </motion.p>
        </div>
      </section>

      {/* Steps Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-24">
            {steps.map((step, index) => (
              <motion.div
                key={step.number}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className={`flex flex-col ${
                  index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'
                } items-center gap-12`}
              >
                {/* Content */}
                <div className="flex-1">
                  <div className="inline-block px-4 py-2 bg-violet-100 dark:bg-violet-900/20 rounded-full text-violet-600 dark:text-violet-400 font-bold mb-4">
                    Step {step.number}
                  </div>
                  <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
                    {step.title}
                  </h2>
                  <p className="text-lg text-gray-600 dark:text-gray-400 mb-6">
                    {step.description}
                  </p>
                  <div className="flex items-center gap-4">
                    <Link to="/register" className="btn bg-gradient-to-r from-pink-500 to-violet-600 hover:from-pink-600 hover:to-violet-700 text-white shadow-md">
                      Get Started Free
                    </Link>
                    <Link to="/pricing" className="text-violet-600 dark:text-violet-400 hover:underline">
                      View Pricing →
                    </Link>
                  </div>
                </div>

                {/* Visual */}
                <div className="flex-1">
                  <div className="card p-12 flex items-center justify-center bg-gradient-to-br from-orange-50 via-pink-50 to-violet-50 dark:from-dark-800 dark:to-dark-700">
                    <div className="text-9xl">{step.image}</div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Platform Connection Guide */}
      <section className="py-20 bg-gray-50 dark:bg-dark-950">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <div className="inline-block px-4 py-2 bg-violet-100 dark:bg-violet-900/20 rounded-full text-violet-600 dark:text-violet-400 font-bold mb-4">
              Step-by-Step Guide
            </div>
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
              How to Connect Each Platform
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
              Connecting is one click — no developer setup or authorization codes. You approve access on the platform&apos;s own secure OAuth 2.0 consent screen, and you&apos;re done. Connect (or later disconnect) a platform from either of two places in the app.
            </p>
          </motion.div>

          {/* The two in-app ways to connect */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.05 }}
            className="grid sm:grid-cols-2 gap-4 mb-10"
          >
            <div className="p-6 bg-white dark:bg-dark-900 border border-gray-200 dark:border-dark-700 rounded-xl">
              <div className="text-violet-600 dark:text-violet-400 font-bold mb-1">1 · Settings → Integrations</div>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Open <span className="font-medium">Settings → Integrations</span>, find the platform, and click <span className="font-medium">Connect</span>. Approve access and you&apos;re linked. <span className="font-medium">Disconnect</span> is available there anytime.
              </p>
            </div>
            <div className="p-6 bg-white dark:bg-dark-900 border border-gray-200 dark:border-dark-700 rounded-xl">
              <div className="text-fuchsia-600 dark:text-fuchsia-400 font-bold mb-1">2 · Analytics → Platforms card</div>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                On the <span className="font-medium">Analytics</span> dashboard, use the <span className="font-medium">Platforms</span> card — click <span className="font-medium">Connect</span> under any platform (or <span className="font-medium">Disconnect</span> a linked one) without leaving your dashboard.
              </p>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
          >
            <PlatformGuideAccordion />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="mt-10 p-6 bg-violet-50 dark:bg-violet-900/20 border border-violet-200 dark:border-violet-800 rounded-xl"
          >
            <h4 className="font-semibold text-violet-800 dark:text-violet-300 mb-2">
              Safe and in your control
            </h4>
            <p className="text-sm text-violet-700 dark:text-violet-400">
              Every connection uses each platform&apos;s official, secure OAuth sign-in. OmniConnX never sees or stores your platform password, and it only requests the access it needs to show your analytics. You can disconnect any platform at any time from <span className="font-medium">Settings → Integrations</span> or the <span className="font-medium">Analytics Platforms</span> card.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20 bg-gray-100 dark:bg-dark-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
              Powerful Features
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
              Everything you need to manage and grow your creator business
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="card p-6"
              >
                <div className="text-5xl mb-4">{feature.icon}</div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                  {feature.title}
                </h3>
                <p className="text-gray-600 dark:text-gray-400">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
              Built For Every Creator
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
              Whether you're just starting out or managing a team, OmniConnX scales with you
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {useCases.map((useCase, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="card p-8"
              >
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                  {useCase.role}
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-6">
                  {useCase.description}
                </p>
                <div className="space-y-2">
                  {useCase.benefits.map((benefit, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-fuchsia-500 rounded-full" />
                      <span className="text-sm text-gray-700 dark:text-gray-300">{benefit}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative overflow-hidden py-20">
        {/* Brand gradient — matches the OmniConnX logo (orange → pink → violet → blue) */}
        <div className="absolute inset-0" style={{ background: BRAND_HERO_GRADIENT }} />
        <div
          className="absolute inset-0 opacity-10"
          style={{ backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)', backgroundSize: '20px 20px' }}
        />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Transform Your Creator Business?
          </h2>
          <p className="text-xl text-white/80 mb-8">
            Join thousands of creators who trust OmniConnX to manage their success
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/register" className="btn bg-white text-violet-700 hover:bg-violet-50 shadow-md">
              Start Free Trial
            </Link>
            <Link to="/pricing" className="btn bg-violet-950/60 text-white hover:bg-violet-950/80 ring-1 ring-white/40">
              View Pricing
            </Link>
          </div>
          <p className="text-white/80 mt-6 text-sm">
            No credit card required • 14-day free trial • Cancel anytime
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white dark:bg-dark-900 border-t border-gray-200 dark:border-dark-700 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-gray-600 dark:text-gray-400">
          <p>&copy; 2026 OmniConnX. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default HowItWorksPage;
